/**
 * In-play maintenance status string — per Match spec §3.1.
 *
 * Lives above the bet-rec card during Live state. The bet-rec card
 * itself is frozen at the Pre-match call; this string communicates
 * the current state of that frozen call relative to live market
 * movement.
 *
 * Three locked verdict states (spec format, copy-shape immutable):
 *
 *   EDGE ALIVE      · with delta (e.g. "+0.14")
 *   EDGE TIGHTENED  · optional brief reason at B7 depth
 *   EDGE COLLAPSED  · required cited reason
 *
 * B6 anti-patterns REJECTED at component design (cannot be reintroduced
 * by Step 6 copy without revision):
 *
 *   - No emoji / hype signals  ("EDGE ALIVE 🔥 +0.14")
 *   - No countdown / urgency   ("HURRY — EDGE ALIVE · 5 MIN LEFT")
 *   - No VIP / tier framing    ("VIP EDGE TIGHTENED")
 *   - No emotional drift       ("💀 EDGE COLLAPSED" / "😭 line shifted")
 *
 * Cross-references A3 element 7 (the freshness pill inside the
 * bet-rec card). Both surface the same engine state in different
 * positions.
 */

import type { EdgeCall, EdgeFreshness } from '@sharp8/types';

interface Props {
  edge: EdgeCall;
}

export function InPlayStatusString({ edge }: Props) {
  const freshness: EdgeFreshness = edge.freshness;

  if (freshness === 'alive') {
    // EDGE ALIVE · +0.14
    // The delta is the current edge expressed as decimal EV
    return (
      <div className="status-string status-string--alive" role="status">
        <span className="status-string__pulse" aria-hidden="true" />
        <span className="status-string__label">EDGE ALIVE</span>
        <span className="status-string__separator">·</span>
        <span className="status-string__delta">+{edge.ev.toFixed(2)}</span>
      </div>
    );
  }

  if (freshness === 'tightened') {
    // EDGE TIGHTENED · optional brief B7-depth reason
    const reason = deriveTightenReason(edge);
    return (
      <div className="status-string status-string--tightened" role="status">
        <span className="status-string__label">EDGE TIGHTENED</span>
        {reason && (
          <>
            <span className="status-string__separator">·</span>
            <span className="status-string__reason">{reason}</span>
          </>
        )}
      </div>
    );
  }

  if (freshness === 'collapsed') {
    // EDGE COLLAPSED — required cited reason
    const reason = deriveCollapseReason(edge);
    return (
      <div className="status-string status-string--collapsed" role="status">
        <span className="status-string__label">EDGE COLLAPSED</span>
        <span className="status-string__em-dash">—</span>
        <span className="status-string__reason">{reason}</span>
      </div>
    );
  }

  return null;
}

// ── Reason derivation (v0.1 algorithmic; Phase C wires brain output) ─

/**
 * v0.1: derive a brief tightened-state reason from the engine's
 * available context. When the Sharp8 brain ships its live-state
 * commentary endpoint, this gets replaced with engine-generated text.
 *
 * Returns null if no clean reason can be derived — the spec allows
 * TIGHTENED with no reason ("optional"); the label alone is valid.
 */
function deriveTightenReason(edge: EdgeCall): string | null {
  // If the citation timestamp is recent (within ~30 min), there's been
  // a verified price move. Show generic "price moved at {book}" — Phase
  // C will replace with named-data B7 commentary.
  const recentCite = edge.reasoning.citations[0];
  if (!recentCite) return null;
  return `price moved at ${edge.book}`;
}

/**
 * v0.1: derive a brief collapsed-state reason. COLLAPSED REQUIRES a
 * cited reason per the spec — never display COLLAPSED without one.
 */
function deriveCollapseReason(edge: EdgeCall): string {
  return `line shifted past tolerance at ${edge.book}`;
}
